import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { signOut } from '@/lib/auth-client';
import {
  UserRole,
  isMapRole,
  getDefaultRouteForRole,
} from '@/lib/roles';
import { useAppAuthState } from '@/lib/useAppAuthState';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  requireMap?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
  requireMap = false,
}) => {
  const location = useLocation();
  const authState = useAppAuthState();

  // Loading state
  if (
    authState.status === "loading_convex_auth" ||
    authState.status === "authenticated_profile_loading"
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (authState.status === "unauthenticated") {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Authenticated but profile provisioning is incomplete
  if (authState.status === "authenticated_no_profile") {
    return <Navigate to="/auth-recovery" state={{ from: location }} replace />;
  }

  if (authState.status === "authenticated_inactive") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md space-y-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Account Disabled</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Your account is currently inactive. Please contact your administrator.
          </p>
          <Button
            variant="outline"
            onClick={() => signOut().then(() => window.location.href = '/')}
          >
            Sign Out
          </Button>
        </div>
      </div>
    );
  }

  const user = authState.user;

  // Check role permissions
  if (allowedRoles && !allowedRoles.includes(user.role as UserRole)) {
    const defaultRoute = getDefaultRouteForRole(user.role);
    if (location.pathname !== defaultRoute) {
      return <Navigate to={defaultRoute} replace />;
    }

    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-4">
            You don't have permission to access this page.
          </p>
          <p className="text-sm text-gray-500">
            Required roles: {allowedRoles.join(', ')}
          </p>
        </div>
      </div>
    );
  }

  // Check map access (only office/supervisor/admin)
  if (requireMap && !isMapRole(user.role)) {
    const defaultRoute = getDefaultRouteForRole(user.role);
    if (location.pathname !== defaultRoute) {
      return <Navigate to={defaultRoute} replace />;
    }

    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Restricted</h1>
          <p className="text-gray-600 mb-4">
            Your role does not have access to the map view. Please use the Job Cards section to create and manage drilling records.
          </p>
          <a href={defaultRoute} className="text-blue-600 hover:underline">
            Go to your workspace
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
